import React, { useState, useEffect, useRef } from 'react';
import { TransactionItem } from '../../models/TransactionItem';
import { TransactionEntity } from '../../models/TransactionEntity';
import { Button, Input } from '../ui';
import AutocompleteInput from './AutocompleteInput';
import { DatePicker } from '../utils';
import { transactionEntitiesService, SimilaritySearchResult } from '../../services/transactionEntitiesService';
import { useAppContext } from '../../contexts/Storage';
import { codeToSymbol } from '../../utils/currencyUtils';

import plusIcon from '../../assets/images/plus_green.png';
import clearIcon from '../../assets/images/close_red.png';

/** Maps form field type to API category */
const FIELD_TO_API_CATEGORY: Record<string, 'action' | 'object' | 'indirect_object' | 'media'> = {
  action: 'action',
  direct_object: 'object',
  indirect_object: 'indirect_object',
  paymentMedia: 'media',
};


interface EnhancedTransactionFormProps {
  formData: Partial<TransactionItem>;
  onFormDataChange: (formData: Partial<TransactionItem>) => void;
  onSubmit: (transaction: Partial<TransactionItem>) => void;
  onCancel?: () => void;
  transaction?: TransactionItem; // For edit mode
  isEditMode?: boolean; // To distinguish between create and edit
  isSubmitting?: boolean;
  onAddEntity?: (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia') => void;
}

const EnhancedTransactionForm: React.FC<EnhancedTransactionFormProps> = ({
  formData: propFormData,
  onFormDataChange,
  onSubmit,
  onCancel,
  transaction,
  isEditMode = false,
  isSubmitting: externalIsSubmitting,
  onAddEntity
}) => {
  const { user, token } = useAppContext();
  const userId = user?.id;

  // Form state
  const [showVendor, setShowVendor] = useState(false);
  const [showQuantity, setShowQuantity] = useState(false);
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  
  // Use external submitting state if provided, otherwise use internal
  const isSubmitting = externalIsSubmitting !== undefined ? externalIsSubmitting : internalIsSubmitting;

  // Selected field for entity addition
  const [selectedField, setSelectedField] = useState<'action' | 'direct_object' | 'indirect_object' | 'paymentMedia'>('action');

  // Track search state per field to control plus icon visibility
  const [fieldSearchState, setFieldSearchState] = useState<Record<string, { hasResults: boolean; hasQuery: boolean; isSearching: boolean }>>({});

    // Get currency from selected record
  const { selectedRecord } = useAppContext();
  const currentRecord = {
    name: selectedRecord?.record_name || 'Record',
    currency: selectedRecord?.record_currency_code || '$'
  };

  // Track which transaction we've initialized to prevent re-init loops
  const initializedTransactionId = useRef<number | null>(null);
  const initializedNewForm = useRef(false);

  // Initialize form with transaction data if in edit mode
  useEffect(() => {
    if (isEditMode && transaction) {
      // Only initialize once per transaction
      if (initializedTransactionId.current === transaction.id) return;
      initializedTransactionId.current = transaction.id ?? null;

      const dateTime =
        transaction.dateTime?.includes(' ') && !transaction.dateTime?.includes('T')
          ? transaction.dateTime.replace(' ', 'T').slice(0, 16)
          : transaction.dateTime;

      const updatedFormData = {
        id: transaction.id,
        dateTime,
        action: transaction.action,
        direct_object: transaction.direct_object,
        indirect_object: transaction.indirect_object,
        qty: transaction.qty,
        paymentMedia: transaction.paymentMedia,
        amount: transaction.amount,
        currency: transaction.currency,
      };
      onFormDataChange(updatedFormData);

      // Show vendor/quantity if they exist
      if (transaction.indirect_object) setShowVendor(true);
      if (transaction.qty && transaction.qty > 1) setShowQuantity(true);
    } else if (!isEditMode && !initializedNewForm.current && !propFormData.dateTime) {
      // Default values for new transaction 
      initializedNewForm.current = true;
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      const defaultFormData = {
        qty: 1,
        currency: currentRecord.currency,
        dateTime: localDateTime
      };
      onFormDataChange(defaultFormData);
    }
  }, [transaction, isEditMode, onFormDataChange, propFormData.dateTime]);

  // Update showVendor and showQuantity based on form data
  useEffect(() => {
    if (propFormData?.indirect_object) {
      setShowVendor(true);
    } else {
      setShowVendor(false);
    }
    if (propFormData?.qty && propFormData.qty > 1) {
      setShowQuantity(true);
    } else {
      setShowQuantity(false);
    }
  }, [propFormData]);

  const handleInputChange = (key: keyof TransactionItem, value: any) => {
    onFormDataChange({ ...propFormData, [key]: value });
  };

  const handleEntityChange = (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia', value: string) => {
    const updatedEntity = new TransactionEntity({
      id: propFormData[field]?.id,
      transaction_entity_name: value
    });
    onFormDataChange({ ...propFormData, [field]: updatedEntity });
    if (!value.trim()) {
      setFieldSearchState((prev) => ({ ...prev, [field]: { hasResults: false, hasQuery: false, isSearching: false } }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    
    if (externalIsSubmitting === undefined) {
      setInternalIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        onSubmit(propFormData);
        setInternalIsSubmitting(false);

        // Reset form if not in edit mode
        if (!isEditMode) {
          const now = new Date();
          const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          onFormDataChange({ qty: 1, currency: currentRecord.currency, dateTime: localDateTime });
          setShowVendor(false);
          setShowQuantity(false);
        }
      }, 1000);
    } else {
      onSubmit(propFormData);
    }
  };

  const handleClearField = (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia') => {
    onFormDataChange({ ...propFormData, [field]: undefined });
  };

  const handleAddEntity = (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia') => {
    setSelectedField(field);
    if (onAddEntity) {
      onAddEntity(field);
    }
  };



  const renderFieldWithControls = (
    label: string,
    field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia',
    placeholder: string,
    required: boolean = false
  ) => {
    const entity = propFormData[field];
    const value = (field === 'action' && entity?.transaction_entity_past_tense)
      ? entity.transaction_entity_past_tense
      : entity?.transaction_entity_name || '';

    // Map field to Autocomplete field type 
    const fieldTypeMap = {
      'action': 'action' as const,
      'direct_object': 'object' as const,
      'indirect_object': 'vendor' as const,
      'paymentMedia': 'payment' as const
    };

    const fieldType = fieldTypeMap[field];

    // Real API call for fetching suggestions via cosine similarity
    const fetchSuggestions = async (query: string): Promise<any[]> => {
      if (!userId || !token) {
        return [];
      }

      const apiCategory = FIELD_TO_API_CATEGORY[field];
      const response = await transactionEntitiesService.findByCosineSimilarity(
        token,
        userId,
        query,
        apiCategory,
        5 // limit
      );

      if (!response.success || !response.data) {
        return [];
      }

      // Map API response to autocomplete format
      return response.data.map((item: SimilaritySearchResult) => ({
        id: item.entity.id,
        name: item.entity.transaction_entity_name,
        description: item.entity.transaction_entity_description || '',
        pastTense: item.entity.transaction_entity_past_tense,
        type: item.entity.transaction_entity_type,
        similarity: item.similarity,
      }));
    };

    const handleSelect = (item: any) => {
      const updatedEntity = new TransactionEntity({
        id: item.id,
        transaction_entity_name: item.name,
        transaction_entity_description: item.description,
        transaction_entity_past_tense: item.pastTense,
      });
      onFormDataChange({ ...propFormData, [field]: updatedEntity });
    };

    const hasSelectedEntity = !!entity?.id;
    const searchState = fieldSearchState[field];
    const showPlusIcon = searchState?.hasQuery;

    return (
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
          {label} {required && <span className="text-danger_dark">*</span>}
        </label>
        <div className="relative flex-1">
          <div className="relative">
            <AutocompleteInput
              value={value}
              onChange={(newValue) => handleEntityChange(field, newValue)}
              onSelect={handleSelect}
              placeholder={placeholder}
              required={required}
              className="w-full"
              fetchSuggestions={fetchSuggestions}
              fieldType={fieldType}
              onSearchStateChange={(state) =>
                setFieldSearchState((prev) => ({ ...prev, [field]: { hasResults: state.hasResults, hasQuery: state.hasQuery, isSearching: state.isSearching } }))
              }
            />
            <div className="absolute right-0 top-0 h-full flex items-center gap-1 pr-2">
              {hasSelectedEntity && (
                <button
                  type="button"
                  onClick={() => handleClearField(field)}
                  className="text-grey_dark hover:text-dark"
                  aria-label={`Clear ${label}`}
                >
                  <img src={clearIcon} alt="Clear" className="w-4 h-4 rotate-180" />
                </button>
              )}
              {showPlusIcon && (
                <button
                  type="button"
                  onClick={() => handleAddEntity(field)}
                  className="text-primary hover:text-primary_variant"
                  aria-label={`Add new ${label}`}
                >
                  <img src={plusIcon} alt="Add" className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2 animate-fadeIn">


      <form onSubmit={handleSubmit}>
        {/* Date & Time - Horizontal Layout */}
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
            Date & Time <span className="text-danger_dark">*</span>
          </label>
          <div className="flex-1">
            <DatePicker
              id="transaction-date"
              value={propFormData.dateTime ?? ""}
              onChange={(value) => {
                handleInputChange('dateTime', value);
                console.log(value);
                
              }}
              required={true}
              type="datetime-local"
            />
          </div>
        </div>

        {/* Action */}
        {renderFieldWithControls('Action', 'action', '', true)}

        {/* Direct Object */}
        {renderFieldWithControls('Direct Object', 'direct_object', '', true)}

        {/* Vendor Toggle - Horizontal Layout */}
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
            
          </label>
          <div className="flex-1">
            <label className="flex items-center cursor-pointer checkbox">
                <input
                  type="checkbox"
                  checked={showVendor}
                  onChange={(e) => {
                    setShowVendor(e.target.checked);
                    if (!e.target.checked) {
                      // Clear vendor field when unchecked
                      handleClearField('indirect_object');
                    }
                  }}
                  className="mr-2 checkbox"
                />
                <span className="checkmark"></span>
                <span className="text-sm text-grey_dark">Add Indirect Object | Vendor?</span>
            </label>
          </div>
        </div>

        {/* Vendor Field (conditionally shown) */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showVendor ? 'max-h-40 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
          {showVendor && (
            renderFieldWithControls('Vendor', 'indirect_object', '', false)
          )}
        </div>

        {/* Quantity Toggle - Horizontal Layout */}
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
            
          </label>
          <div className="flex-1">
            <label className="flex items-center cursor-pointer checkbox">
                <input
                  type="checkbox"
                  checked={showQuantity}
                  onChange={(e) => {
                    setShowQuantity(e.target.checked);
                    if (!e.target.checked) {
                      // Reset quantity to 1 when unchecked
                      handleInputChange('qty', 1);
                    }
                  }}
                  className="mr-2 checkbox"
                />
                <span className="checkmark"></span>
                <span className="text-sm text-grey_dark">Add Quantity?</span>
            </label>
          </div>
        </div>

        {/* Quantity Field (conditionally shown) - Horizontal Layout */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showQuantity ? 'max-h-40 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
          {showQuantity && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                Quantity <span className="text-danger_dark">*</span>
              </label>
              <div className="flex-1">
                <Input
                  type="number"
                  attributes={{
                    step: '0.01',
                    placeholder: '',
                    value: propFormData.qty || '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('qty', parseFloat(e.target.value) || 1),
                    required: true
                  }}
                  className="w-full"
                />
              </div>
            </div>
        )}
        </div>

        {/* Payment Media */}
        {renderFieldWithControls('Payment Media', 'paymentMedia', '', true)}

        {/* Total Amount - Horizontal Layout */}
        <div className="flex items-center gap-4 mb-6">
          <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
            Total Amount <span className="text-danger_dark">*</span>
          </label>
          <div className="flex-1">
            <div className="w-full flex items-center gap-2">
                            <span className="text-sm font-medium text-grey_dark min-w-[20px]">
                {propFormData.currency ? codeToSymbol(propFormData.currency) : codeToSymbol(currentRecord.currency)}
              </span>
              <Input
                type="number"
                attributes={{
                  step: '0.01',
                  placeholder: '',
                  value: propFormData.amount || '',
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('amount', parseFloat(e.target.value) || 0),
                  required: true
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="filled-primary"
          className="w-full flex items-center justify-center"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEditMode ? 'Updating...' : 'Saving...'}
            </>
          ) : (
            <>
              <img
                src={plusIcon}
                alt={isEditMode ? 'Update' : 'Add'}
                className="w-4 h-4 mr-2"
              />
              {isEditMode ? 'Update Transaction' : 'Save Transaction'}
            </>
          )}
        </Button>
      </form>


    </div>
  );
};

export default EnhancedTransactionForm;